import pRetry from 'p-retry';
import { buildUserIdPayload, hasUserId } from '../utils/userIdPayload';

const requireUserId = (userId) => {
    if (!hasUserId(userId)) {
        throw new Error('Не удалось определить пользователя. Откройте приложение из MAX или Telegram заново.');
    }
};

async function readResponse(response, { preserveAuthError = false } = {}) {
    const data = await response.json().catch(() => ({}));
    const isStructuredAuthError = response.status === 401 && data.result?.error;
    if (!response.ok && !(preserveAuthError && isStructuredAuthError)) {
        throw new Error(data.detail || data.error || `Request failed: ${response.status}`);
    }
    return data;
}

export async function login({ platform, userId, login, password, number, language }) {
    requireUserId(userId);
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...buildUserIdPayload(platform, userId),
                indifier: login,
                password,
                number,
                language,
            }),
        },
    );
    return await readResponse(response, { preserveAuthError: true });
}

export async function callback({
    platform,
    userId,
    access_token,
    contract_number,
    language,
    first_name,
    middle_name,
    last_name,
    phone,
    first,
    session,
}) {
    requireUserId(userId);
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/callback`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...buildUserIdPayload(platform, userId),
                access_token,
                contract_number,
                language: language || null,
                first_name: first_name || null,
                middle_name: middle_name || null,
                last_name: last_name || null,
                phone: phone || null,
                first: first || null,
                session: session || null,
            }),
        },
    );
    return await readResponse(response);
}

export async function callback_less({
    platform,
    userId,
    access_token,
    contract_number,
    language,
    first_name,
    middle_name,
    last_name,
    phone,
    first,
    session,
}) {
    requireUserId(userId);
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/callback_less`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...buildUserIdPayload(platform, userId),
                contract_number,
                first_name: first_name || null,
                language: language || null,
                middle_name: middle_name || null,
                last_name: last_name || null,
                phone: phone || null,
                first: first || null,
                session: session || null,
                access_token,
            }),
        },
    );
    return await readResponse(response);
}

export const getContractsFullByID = async ({ platform, userId }) => {
    requireUserId(userId);
    return await pRetry(
        async () => {
            return await _getContractsFullByID({ platform, userId });
        },
        { retries: 5 },
    );
};

export const _getContractsFullByID = async ({ platform, userId }) => {
    requireUserId(userId);
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/get_contracts`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(buildUserIdPayload(platform, userId)),
        },
    );

    if (response.status === 404) {
        return [];
    }

    const data = await readResponse(response);

    return data.result ?? [];
};

/**
 * Отключить договор (деструктивное действие).
 * @param {{ platform: string, userId: string, contract_number: string }} params
 * @returns {Promise<void>}
 */
export const deleteContract = async ({ platform, userId, contract_number }) => {
    requireUserId(userId);
    return await pRetry(
        async () => {
            return await _deleteContract({ platform, userId, contract_number });
        },
        { retries: 5 },
    );
};

/**
 * @param {{ platform: string, userId: string, contract_number: string }} params
 * @returns {Promise<void>}
 */
export const _deleteContract = async ({ platform, userId, contract_number }) => {
    requireUserId(userId);
    const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/delete_contract`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...buildUserIdPayload(platform, userId),
                contract_number,
            }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to delete contract ${contract_number}: ${response.status} ${response.statusText}`,
        );
    }
};
